import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { estimateClaim } from '@api';
import {
  RespCode, ErrorCode, SessionStorageKey,
} from '@constants';
import {
  useQuery, useEffectOnce, useBehaviorData,
} from '@hooks';
import { logError } from '@log';
import {
  setCustomerInfo, setPolicyInfo, setClaimInfo,
} from '@store';

/** 输入框状态 */
const InputStatus = {
  /** 初始化 */
  Init: 0,
  /** 修改过 */
  Changed: 1,
};

// eslint-disable-next-line no-control-regex
const regEmail = /(?:[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a -z0-9])?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}( ?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-zA-Z0-9-]*[a-z0 ]-9]：(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+ )\])/;
// const regOrderId = /^([0-9])+/;

/** 设置 email */
const onChangeEmail = ({
  event,
  setEmail,
  emailStatus,
  setEmailStatus,
}) => {
  const { value } = event.target;
  setEmail(value.trim());
  if (emailStatus !== InputStatus.Changed) {
    setEmailStatus(InputStatus.Changed);
  }
};

/** 设置 order id */
const onChangeOrderId = ({
  event,
  setOrderId,
  orderStatus,
  setOrderStatus,
}) => {
  const { value } = event.target;
  setOrderId(value.trim());
  if (orderStatus !== InputStatus.Changed) {
    setOrderStatus(InputStatus.Changed);
  }
};

/** 提交信息 */
const submit = async ({
  email,
  orderId,
  setIsInvalidBarShow,
  setIsLoading,
  navigate,
  dispatch,
  setReportData,
}) => {
  try {
    setIsLoading(true);
    setReportData({ loginTime: Date.now() });
    dispatch(setPolicyInfo({ email, orderNumber: orderId }));
    const resp = await estimateClaim(email, orderId);
    if (!resp) {
      setIsInvalidBarShow({ show: true });
      setIsLoading(false);
      return;
    }
    const { code, data } = resp;
    if (code !== RespCode.Success) {
      // eslint-disable-next-line max-len
      const errorMap = [ErrorCode.Expired, ErrorCode.Total, ErrorCode.Cancelled, ErrorCode.HasOldClaim, ErrorCode.CoverdItemEmpty];
      if (errorMap.includes(code)) {
        navigate('/17track/error', { state: { code } });
      } else if (code === ErrorCode.Nothing) {
        const msg = 'No Seel covers found for this order. Please double check or contact support for help.';
        setIsInvalidBarShow({ show: true, message: msg });
      } else {
        navigate('/17track/error', { state: { code: ErrorCode.Wrong } });
      }
    } else {
      setIsInvalidBarShow({ show: false });
      const { policyList = [] } = data;
      dispatch(setCustomerInfo(data));
      // 有理赔记录直接跳转反馈页面
      if (data.claimList?.length > 0) {
        dispatch(setClaimInfo({ type: '', ...policyList[0] }));

        navigate(`/17track/feedback?claimId=${data.claimList[0]?.claimId}`);
        return;
      }
      navigate('/17track/options');
    }
    setIsLoading(false);
  } catch (error) {
    logError(error);
  }
};

export const useLogin = () => {
  const {
    query: {
      email: defaultEmail = '', buyer_protection_id: defaultProtection = '', order_number: defaultOrderId = '',
    },
  } = useQuery();
  /** email */
  const [email, setEmail] = useState(defaultEmail);
  /** order id */
  const [orderId, setOrderId] = useState(defaultOrderId || defaultProtection);
  /** email 输入框状态 */
  const [emailStatus, setEmailStatus] = useState(
    defaultEmail ? InputStatus.Changed : InputStatus.Init,
  );
  /** order id 输入框状态 */
  const [orderStatus, setOrderStatus] = useState(
    (defaultOrderId || defaultProtection) ? InputStatus.Changed : InputStatus.Init,
  );
  /** 是否显示错误提示 */
  const [isInvalidBarShow, setIsInvalidBarShow] = useState({ show: false, message: '' });
  /** 按钮是否 loading */
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const setReportData = useBehaviorData();
  /** email 是否合法 */
  const isEmailValid = regEmail.test(email);
  /** order id 是否合法 */
  const isOrderIdValid = orderId.length > 0;
  /** 是否显示 email 非法提示 */
  const isEmailShownValid = emailStatus === InputStatus.Init || isEmailValid;
  /** 是否显示 order id 非法提示 */
  const isOrderShownValid = orderStatus === InputStatus.Init || isOrderIdValid;
  /** email 非法提示文案 */
  const emailHelperText = email ? 'Invalid email address' : 'Email is required';
  /** order id 非法提示文案 */
  const orderIdHelperText = orderId ? 'Invalid Worry-Free Purchase ID / Order Number' : 'Worry-Free Purchase ID / Order Number is required';

  /** 提交按钮的状态，只有当2个都有效的情况下，才可以提交 */
  const isBtnDisable = !(isEmailValid && isOrderIdValid);

  useEffectOnce(() => {
    // 如果 url 上带有参数，自动登录
    const hasLogin = sessionStorage.getItem(SessionStorageKey.HasAutoLogin);
    if (!isBtnDisable && !hasLogin) {
      submit({
        email,
        orderId,
        setReportData,
        setIsInvalidBarShow,
        setIsLoading,
        navigate,
        dispatch,
      });
      sessionStorage.setItem(SessionStorageKey.HasAutoLogin, 'true');
    }
  });

  return {
    email,
    orderId,
    isBtnDisable,
    emailHelperText: isEmailShownValid ? ' ' : emailHelperText,
    orderIdHelperText: isOrderShownValid ? ' ' : orderIdHelperText,
    isEmailValid: isEmailShownValid,
    isOrderIdValid: isOrderShownValid,
    isInvalidBarShow,
    isLoading,
    onChangeEmail: (event) => onChangeEmail({
      event,
      setEmail,
      emailStatus,
      setEmailStatus,
    }),
    onChangeOrderId: (event) => onChangeOrderId({
      event,
      setOrderId,
      orderStatus,
      setOrderStatus,
    }),
    onClickBtn: () => submit({
      email,
      orderId,
      setReportData,
      setIsInvalidBarShow,
      setIsLoading,
      navigate,
      dispatch,
    }),
  };
};
